import {useMemo} from 'react';

import {ExtraPrice, OrderLine} from '../transport/apiServiceResponse';
import {getProductDisplayRules, ProductDisplayRules} from '../utils/rewards';

import useStorePriceDisplayConditions from './usePriceDisplayConditions';

export type ExternalSellType = ExtraPrice['external_sell_type'];

export type ProductDisplayInfo = {
  externalSellType: ExternalSellType;
  productDisplayRules: ProductDisplayRules;
  showMemberPriceLabel: boolean;
  showNonMemberPriceText: boolean;
};

/**
 * Pure function version for use outside React components
 * (e.g., when product may be null and conditional hooks aren't allowed)
 */
export function getProductDisplayInfo(
  product: OrderLine,
  showMemberPrice: {privatePricing: boolean; storeLoyaltyEnabled: boolean},
): ProductDisplayInfo {
  const productDisplayRules = getProductDisplayRules(product);

  // Determine correct sell type based on which pricing is actually better
  // Private takes precedence if both are available
  const externalSellType: ExternalSellType = productDisplayRules.presentAsPrivateMemberProduct
    ? 'private'
    : productDisplayRules.presentAsPublicMemberProduct
      ? 'public'
      : undefined;

  // DIS-1590: the badge is an advert, so the public arm follows the STORE
  // (`storeLoyaltyEnabled`), not the shopper's entitlement. Gating it on
  // `publicPricing` dropped the badge for every logged-out shopper. The private arm
  // stays on entitlement: a targeted price is only ever shown to its recipient.
  const showMemberPriceLabel =
    (externalSellType === 'public' &&
      showMemberPrice.storeLoyaltyEnabled &&
      productDisplayRules.presentAsPublicMemberProduct) ||
    (externalSellType === 'private' &&
      showMemberPrice.privatePricing &&
      productDisplayRules.presentAsPrivateMemberProduct);

  // showNonMemberPriceText mirrors showMemberPriceLabel
  // (show "Non-members Price" when we're showing member pricing)
  const showNonMemberPriceText = showMemberPriceLabel;

  return {
    externalSellType,
    productDisplayRules,
    showMemberPriceLabel,
    showNonMemberPriceText,
  };
}

/**
 * Hook to derive product display information from an OrderLine.
 * Consolidates logic for member pricing display that was previously
 * scattered across MembersPriceLabel and SecondaryCalloutLabel.
 *
 * @param product - The product/order line to analyze
 * @returns Display info including externalSellType, productDisplayRules,
 *          showMemberPriceLabel, and showNonMemberPriceText
 */
export function useProductDisplayInfo(product: OrderLine): ProductDisplayInfo {
  const {showMemberPrice} = useStorePriceDisplayConditions();

  return useMemo(() => getProductDisplayInfo(product, showMemberPrice), [product, showMemberPrice]);
}
